<template>
  <div class="user-mobile-container">
    <!-- 操作栏 -->
    <div class="operation-bar">
      <el-button type="primary" size="large" @click="handleAdd" class="add-btn">
        <i class="el-icon-plus"></i> 添加用户
      </el-button>
    </div>

    <!-- 移动端卡片列表 -->
    <div class="card-list" v-loading="loading">
      <div 
        v-for="user in userList" 
        :key="user.id" 
        class="user-card"
      >
        <div class="card-header">
          <div class="user-avatar">
            <i class="el-icon-user"></i>
          </div>
          <div class="user-info">
            <div class="username">{{ user.username }}</div>
            <div class="name">{{ user.name }}</div>
          </div>
          <div class="user-status">
            <el-tag :type="user.status === 1 ? 'success' : 'danger'" size="mini">
              {{ user.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </div>
        </div>
        
        <div class="card-content">
          <div class="info-row">
            <span class="label">性别:</span>
            <span class="value">{{ user.sex === '1' ? '男' : '女' }}</span>
          </div>
          <div class="info-row">
            <span class="label">生日:</span>
            <span class="value">{{ user.birthday || '未设置' }}</span>
          </div>
          <div class="info-row">
            <span class="label">角色:</span>
            <span class="value">{{ user.role === 0 ? '管理员' : '普通用户' }}</span>
          </div>
          <div class="info-row score-row">
            <span class="label">积分:</span>
            <span 
              class="score-value" 
              @click="handleViewScore(user)"
              :class="{ 'clickable': user.total_score }"
            >
              {{ user.total_score || 0 }}
            </span>
          </div>
        </div>
        
        <div class="card-actions">
          <el-button 
            size="mini" 
            type="primary" 
            @click="handleEdit(user)"
            class="action-btn"
          >
            编辑
          </el-button>
          <el-button 
            size="mini" 
            type="success" 
            @click="handleEditPassword(user)"
            class="action-btn"
          >
            改密码
          </el-button>
          <el-button 
            size="mini" 
            :type="user.status === 1 ? 'warning' : 'success'"
            @click="handleToggleStatus(user)"
            class="action-btn"
          >
            {{ user.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button 
            size="mini" 
            type="danger" 
            @click="handleDelete(user)"
            class="action-btn"
          >
            删除
          </el-button>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div v-if="userList.length === 0 && !loading" class="empty-state">
        <i class="el-icon-user"></i>
        <p>暂无用户数据</p>
      </div>
    </div>

    <!-- 添加用户对话框 -->
    <el-dialog
      title="添加用户"
      :visible.sync="addDialogVisible"
      width="90%"
      center
      :close-on-click-modal="false"
      append-to-body
      custom-class="mobile-dialog"
    >
      <el-form
        ref="addForm"
        :model="addForm"
        :rules="addFormRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="addForm.username" ref="usernameInput"></el-input>
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="addForm.password" type="password"></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="addForm.name"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="addForm.sex">
            <el-radio label="1">男</el-radio>
            <el-radio label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="生日" prop="birthday">
          <el-date-picker
            v-model="addForm.birthday"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-radio-group v-model="addForm.role">
            <el-radio :label="0">管理员</el-radio>
            <el-radio :label="1">普通用户</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false" size="large">取 消</el-button>
        <el-button type="primary" @click="submitAdd" size="large">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog
      title="修改密码"
      :visible.sync="passwordDialogVisible"
      width="90%"
      center
      :close-on-click-modal="false"
      append-to-body
      custom-class="mobile-dialog"
    >
      <el-form
        ref="passwordForm"
        :model="passwordForm"
        :rules="passwordFormRules"
        label-width="80px"
      >
        <el-form-item label="新密码" prop="password">
          <el-input v-model="passwordForm.password" type="password" ref="passwordInput"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false" size="large">取 消</el-button>
        <el-button type="primary" @click="submitPassword" size="large">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑用户对话框 -->
    <el-dialog
      title="编辑用户"
      :visible.sync="editDialogVisible"
      width="90%"
      center
      :close-on-click-modal="false"
      append-to-body
      custom-class="mobile-dialog"
    >
      <el-form
        ref="editForm"
        :model="editForm"
        :rules="editFormRules"
        label-width="80px"
      >
        <el-form-item label="用户名">
          <el-input v-model="editForm.username" disabled></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="editForm.name" ref="nameInput"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="editForm.sex">
            <el-radio label="1">男</el-radio>
            <el-radio label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="生日" prop="birthday">
          <el-date-picker
            v-model="editForm.birthday"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-radio-group v-model="editForm.role">
            <el-radio :label="0">管理员</el-radio>
            <el-radio :label="1">普通用户</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false" size="large">取 消</el-button>
        <el-button type="primary" @click="submitEdit" size="large">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 积分详情对话框 -->
    <el-dialog
      title="积分详情"
      :visible.sync="scoreDialogVisible"
      width="95%"
      center
      :close-on-click-modal="false"
      append-to-body
      custom-class="mobile-dialog"
    >
      <div v-if="currentUserScore" class="score-detail-mobile">
        <div class="user-info">
          <h3>
            {{ currentUserScore.name }} ({{ currentUserScore.username }})
            <el-button 
              type="text" 
              icon="el-icon-data-analysis"
              @click="handleScoreTrend"
              style="margin-left: 8px; color: #409EFF; font-size: 12px;">
              积分分析
            </el-button>
          </h3>
        </div>
        
        <el-divider></el-divider>
        
        <div class="score-breakdown">
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-trophy"></i>
              参赛积分
            </div>
            <div class="score-value">{{ currentUserScore.game_score || 0 }}</div>
            <div class="score-desc">每参加一场比赛积分+1</div>
          </div>
          
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-check"></i>
              题目积分
            </div>
            <div class="score-value">{{ currentUserScore.question_score || 0 }}</div>
            <div class="score-desc">每AC一道题积分+1，AK额外+5</div>
          </div>
          
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-medal"></i>
              排名积分
            </div>
            <div class="score-value">{{ currentUserScore.rank_score || 0 }}</div>
            <div class="score-desc">前10%+1，前5%+2，前1%+3，第一名+5</div>
          </div>
          
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-edit"></i>
              订正积分
            </div>
            <div class="score-value">{{ currentUserScore.fix_score || 0 }}</div>
            <div class="score-desc">每订正一道题积分+1</div>
          </div>
          
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-video-camera"></i>
              复盘积分
            </div>
            <div class="score-value">{{ currentUserScore.replay_score || 0 }}</div>
            <div class="score-desc">每复盘一场比赛积分+2</div>
          </div>
          
          <div class="score-item">
            <div class="score-label">
              <i class="el-icon-document"></i>
              文章积分
            </div>
            <div class="score-value">{{ currentUserScore.article_score || 0 }}</div>
            <div class="score-desc">每一篇文章积分+2</div>
          </div>
        </div>
        
        <el-divider></el-divider>
        
        <div class="total-score">
          <div class="total-label">总积分</div>
          <div class="total-value">{{ currentUserScore.total_score || 0 }}</div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="scoreDialogVisible = false" size="large">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { isMobile } from '@/utils/device'

export default {
  name: 'UserMobile',
  data() {
    return {
      loading: false,
      userList: [],
      
      // 添加用户相关数据
      addDialogVisible: false,
      addForm: {
        username: '',
        password: '',
        name: '',
        sex: '1',
        birthday: '',
        role: 1
      },
      addFormRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        sex: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ],
        birthday: [
          { required: true, message: '请选择生日', trigger: 'change' }
        ],
        role: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ]
      },
      
      // 修改密码相关数据
      passwordDialogVisible: false,
      passwordForm: {
        userId: null,
        password: ''
      },
      passwordFormRules: {
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' }
        ]
      },
      
      // 积分相关数据
      scoreDialogVisible: false,
      currentUserScore: null,
      scoreList: [],
      
      // 编辑用户相关数据
      editDialogVisible: false,
      editForm: {
        id: null,
        username: '',
        name: '',
        sex: '1',
        birthday: '',
        role: 1
      },
      editFormRules: {
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        sex: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ],
        birthday: [
          { required: true, message: '请选择生日', trigger: 'change' }
        ],
        role: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    // 获取用户列表
    async fetchData() {
      this.loading = true
      try {
        // 并行获取用户列表和积分数据
        const [userResponse, scoreResponse] = await Promise.all([
          request({
            url: '/api/user/list',
            method: 'get'
          }),
          request({
            url: '/api/user/score',
            method: 'get'
          })
        ])
        
        if (userResponse.code === 200) {
          this.userList = userResponse.data.items || []
        } else {
          throw new Error(userResponse.message || '获取用户列表失败')
        }
        
        if (scoreResponse.code === 200) {
          this.scoreList = scoreResponse.data.items || []
          // 将积分数据合并到用户列表中
          this.mergeScoreData()
        } else {
          console.warn('获取积分数据失败:', scoreResponse.message)
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.$message.error(error.message || '获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 合并积分数据到用户列表
    mergeScoreData() {
      const scoreMap = {}
      this.scoreList.forEach(score => {
        scoreMap[score.username] = score
      })
      
      this.userList.forEach(user => {
        if (scoreMap[user.username]) {
          Object.assign(user, scoreMap[user.username])
        }
      })
    },
    
    // 添加用户相关方法
    handleAdd() {
      this.addForm = {
        username: '',
        password: '',
        name: '',
        sex: '1',
        birthday: '',
        role: 1
      }
      this.addDialogVisible = true
      this.$nextTick(() => {
        // 自动聚焦到用户名输入框
        setTimeout(() => {
          this.$refs.usernameInput && this.$refs.usernameInput.focus()
        }, 100)
      })
    },
    
    async submitAdd() {
      this.$refs.addForm.validate(async valid => {
        if (valid) {
          try {
            const response = await request({
              url: '/api/user/add',
              method: 'post',
              data: this.addForm
            })
            if (response.code === 200) {
              this.$message.success('添加用户成功')
              this.addDialogVisible = false
              this.fetchData()
            } else {
              this.$message.error(response.message || '添加用户失败')
            }
          } catch (error) {
            console.error('添加用户失败:', error)
            this.$message.error(error.message || '添加用户失败')
          }
        }
      })
    },
    
    // 修改密码相关方法
    handleEditPassword(row) {
      this.passwordForm = {
        userId: row.id,
        password: ''
      }
      this.passwordDialogVisible = true
      this.$nextTick(() => {
        // 自动聚焦到密码输入框
        setTimeout(() => {
          this.$refs.passwordInput && this.$refs.passwordInput.focus()
        }, 100)
      })
    },
    
    async submitPassword() {
      this.$refs.passwordForm.validate(async valid => {
        if (valid) {
          try {
            await request({
              url: `/api/user/${this.passwordForm.userId}/password`,
              method: 'put',
              data: { password: this.passwordForm.password }
            })
            this.$message.success('修改密码成功')
            this.passwordDialogVisible = false
          } catch (error) {
            console.error('修改密码失败:', error)
          }
        }
      })
    },
    
    // 更新用户状态
    async handleToggleStatus(row) {
      try {
        const newStatus = row.status === 1 ? 0 : 1
        const actionText = newStatus === 1 ? '启用' : '禁用'
        
        await this.$confirm(`确认要${actionText}该用户吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await request({
          url: `/api/user/${row.id}/status`,
          method: 'put',
          data: { status: newStatus }
        })
        
        this.$message.success(`${actionText}成功`)
        this.fetchData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新用户状态失败:', error)
        }
      }
    },
    
    // 删除用户
    async handleDelete(row) {
      try {
        await this.$confirm('确认要删除该用户吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        await request({
          url: `/api/user/${row.id}`,
          method: 'delete'
        })
        
        this.$message.success('删除成功')
        this.fetchData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除用户失败:', error)
        }
      }
    },
    
    // 编辑用户相关方法
    handleEdit(row) {
      this.editForm = {
        id: row.id,
        username: row.username,
        name: row.name,
        sex: row.sex,
        birthday: row.birthday,
        role: Number(row.role)
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        // 自动聚焦到姓名输入框
        setTimeout(() => {
          this.$refs.nameInput && this.$refs.nameInput.focus()
        }, 100)
      })
    },
    
    async submitEdit() {
      this.$refs.editForm.validate(async valid => {
        if (valid) {
          try {
            const submitData = {
              ...this.editForm,
              role: Number(this.editForm.role)
            }
            const response = await request({
              url: `/api/user/${this.editForm.id}`,
              method: 'put',
              data: submitData
            })
            if (response.code === 200) {
              this.$message.success('更新用户成功')
              this.editDialogVisible = false
              this.fetchData()
            } else {
              this.$message.error(response.message || '更新用户失败')
            }
          } catch (error) {
            console.error('更新用户失败:', error)
            this.$message.error(error.message || '更新用户失败')
          }
        }
      })
    },
    
    // 查看积分详情
    handleViewScore(row) {
      this.currentUserScore = row
      this.scoreDialogVisible = true
    },
    
    // 跳转到积分趋势分析
    handleScoreTrend() {
      this.$router.push({
        path: '/analysis/score-trend',
        query: {
          username: this.currentUserScore.username,
          name: this.currentUserScore.name
        }
      })
      this.scoreDialogVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.user-mobile-container {
  padding: 16px;
  min-height: 100vh;
  background-color: #f5f7fa;

  .operation-bar {
    margin-bottom: 20px;
    text-align: center;
    
    .add-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      border-radius: 8px;
    }
  }

  .card-list {
    .user-card {
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid #e4e7ed;

      .card-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #f0f0f0;

        .user-avatar {
          width: 48px;
          height: 48px;
          background: #f0f9ff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;

          i {
            font-size: 24px;
            color: #409eff;
          }
        }

        .user-info {
          flex: 1;

          .username {
            font-size: 16px;
            font-weight: 600;
            color: #303133;
            margin-bottom: 4px;
          }

          .name {
            font-size: 14px;
            color: #606266;
          }
        }

        .user-status {
          margin-left: auto;
        }
      }

      .card-content {
        margin-bottom: 16px;

        .info-row {
          display: flex;
          margin-bottom: 8px;
          align-items: center;

          .label {
            width: 60px;
            font-size: 14px;
            color: #909399;
            flex-shrink: 0;
          }

          .value {
            font-size: 14px;
            color: #303133;
            flex: 1;
          }
          
          &.score-row {
            .score-value {
              font-size: 16px;
              font-weight: bold;
              color: #409EFF;
              
              &.clickable {
                cursor: pointer;
                text-decoration: underline;
              }
            }
          }
        }
      }

      .card-actions {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;

        .action-btn {
          flex: 1;
          height: 36px;
          font-size: 12px;
          min-width: 0;
          padding: 0 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          
          // 确保所有按钮内容居中
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      color: #909399;

      i {
        font-size: 48px;
        margin-bottom: 16px;
        display: block;
      }

      p {
        font-size: 16px;
        margin: 0;
      }
    }
  }

  // 移动端对话框样式
  .mobile-dialog {
    .el-dialog {
      margin: 10vh auto !important;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

      .el-dialog__header {
        padding: 20px 20px 16px;
        background-color: #f5f7fa;
        border-bottom: 1px solid #e4e7ed;
        text-align: center;
      }

      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }

      .el-dialog__body {
        padding: 20px;
      }

      .el-dialog__footer {
        padding: 16px 20px 20px;
        border-top: 1px solid #f0f0f0;
        text-align: center;

        .el-button {
          width: 45%;
          height: 44px;
          font-size: 16px;
        }
      }
    }
  }
}

/* 移动端积分详情样式 */
.score-detail-mobile {
  padding: 10px 0;
}

.user-info {
  text-align: center;
  margin-bottom: 20px;
}

.user-info h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
}

.score-breakdown {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.score-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  border: 1px solid #e9ecef;
}

.score-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #606266;
  margin-bottom: 6px;
}

.score-label i {
  margin-right: 4px;
  font-size: 14px;
  color: #409EFF;
}

.score-value {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.score-desc {
  font-size: 10px;
  color: #909399;
  line-height: 1.3;
}

.total-score {
  background: linear-gradient(135deg, #409EFF, #67C23A);
  color: white;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  margin-top: 20px;
}

.total-label {
  font-size: 14px;
  margin-bottom: 6px;
  opacity: 0.9;
}

.total-value {
  font-size: 24px;
  font-weight: bold;
}

// 响应式调整
@media (max-width: 480px) {
  .user-mobile-container {
    padding: 12px;

    .card-list .user-card {
      padding: 12px;
      margin-bottom: 12px;

      .card-actions {
        gap: 6px;
        
        .action-btn {
          height: 40px;
          font-size: 13px;
          padding: 0 2px;
        }
      }
    }
  }
}
</style> 